import React, { useEffect, useMemo, useState } from 'react';
import { X, MessageCircle } from 'lucide-react';
import { apiUrl } from '../config/api';

type FeedbackResult = 'submitted' | 'dismissed';

interface CourseFeedbackModalProps {
  isOpen: boolean;
  userEmail?: string;
  origin?: 'module' | 'navbar';
  onComplete: (result: FeedbackResult) => void;
}

interface CourseFeedbackFormData {
  courseSatisfaction: string;
  pdfHelpfulness: string;
  implementationConfidence: string;
  feedbackTimeliness: string;
  feedbackQuality: string;
  feedbackApplicability: string;
  highlight?: string;
  improvementIdeas?: string;
  followUpInterest: string;
  followUpContact: string;
}

const initialFormState: CourseFeedbackFormData = {
  courseSatisfaction: '',
  pdfHelpfulness: '',
  implementationConfidence: '',
  feedbackTimeliness: '',
  feedbackQuality: '',
  feedbackApplicability: '',
  highlight: '',
  improvementIdeas: '',
  followUpInterest: 'No',
  followUpContact: ''
};

export function CourseFeedbackModal({
  isOpen,
  userEmail,
  origin = 'navbar',
  onComplete
}: CourseFeedbackModalProps) {
  const [formData, setFormData] = useState<CourseFeedbackFormData>({
    ...initialFormState,
    followUpContact: userEmail || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'form' | 'thanks'>('form');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...initialFormState,
        followUpContact: userEmail || ''
      });
      setErrors({});
      setLoading(false);
      setView('form');
    }
  }, [isOpen, userEmail]);

  const ratingOptions = useMemo(
    () => [
      { value: '1', label: '1 - Not at all' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
      { value: '4', label: '4' },
      { value: '5', label: '5 - Extremely' }
    ],
    []
  );

  const validate = () => {
    const newErrors: Record<string, string> = {};

    const requiredFields: Array<keyof CourseFeedbackFormData> = [
      'courseSatisfaction',
      'pdfHelpfulness',
      'implementationConfidence',
      'feedbackTimeliness',
      'feedbackQuality',
      'feedbackApplicability'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'Please select an option';
      }
    });

    if (formData.followUpInterest === 'Yes' && !formData.followUpContact.trim()) {
      newErrors.followUpContact = 'Please provide the best email to reach you';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof CourseFeedbackFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(apiUrl('/api/module3-feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          ...formData,
          userEmail: userEmail || formData.followUpContact || 'anonymous',
          submittedAt: new Date().toISOString(),
          moduleId: 3, // retain module id for backward compatibility
          feedbackVersion: 'course-v2'
        })
      });

      const result = await response.json();

      if (result.success) {
        setView('thanks');
      } else {
        alert(result.message || 'Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting course feedback:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    if (view === 'thanks') {
      onComplete('submitted');
      return;
    }
    onComplete('dismissed');
  };

  const handleThankYouContinue = () => {
    onComplete('submitted');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.titleWrapper}>
            <MessageCircle size={22} color="var(--orange)" />
            <h2 style={styles.title}>
              Share Your Experience ({origin === 'module' ? 'Quick Pulse' : 'Anytime Feedback'})
            </h2>
          </div>
          <button
            onClick={handleDismiss}
            style={styles.closeBtn}
            aria-label="Close feedback"
          >
            <X size={20} />
          </button>
        </div>

        {view === 'form' ? (
          <form onSubmit={handleSubmit} style={styles.form}>
            <p style={styles.intro}>
              This takes less than 2 minutes. Your answers help us tune the experience for senior recruiters like you.
            </p>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                How valuable has the free course been for you so far? <span style={styles.required}>*</span>
              </label>
              <div style={styles.optionsRow}>
                {ratingOptions.map(option => (
                  <label key={option.value} style={styles.radioOption}>
                    <input
                      type="radio"
                      name="courseSatisfaction"
                      value={option.value}
                      checked={formData.courseSatisfaction === option.value}
                      onChange={e => handleChange('courseSatisfaction', e.target.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.courseSatisfaction && <p style={styles.error}>{errors.courseSatisfaction}</p>}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                How useful are the downloadable PDFs and templates? <span style={styles.required}>*</span>
              </label>
              <div style={styles.optionsRow}>
                {ratingOptions.map(option => (
                  <label key={option.value} style={styles.radioOption}>
                    <input
                      type="radio"
                      name="pdfHelpfulness"
                      value={option.value}
                      checked={formData.pdfHelpfulness === option.value}
                      onChange={e => handleChange('pdfHelpfulness', e.target.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.pdfHelpfulness && <p style={styles.error}>{errors.pdfHelpfulness}</p>}
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                After completing the exercises, how confident do you feel implementing the ideas in real searches? <span style={styles.required}>*</span>
              </label>
              <div style={styles.optionsRow}>
                {ratingOptions.map(option => (
                  <label key={option.value} style={styles.radioOption}>
                    <input
                      type="radio"
                      name="implementationConfidence"
                      value={option.value}
                      checked={formData.implementationConfidence === option.value}
                      onChange={e => handleChange('implementationConfidence', e.target.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.implementationConfidence && <p style={styles.error}>{errors.implementationConfidence}</p>}
            </div>

            <div style={styles.fieldGroupGrid}>
              <div>
                <label style={styles.label}>
                  Are our feedback responses arriving in time for you? <span style={styles.required}>*</span>
                </label>
                <div style={styles.optionsRow}>
                  {ratingOptions.map(option => (
                    <label key={option.value} style={styles.radioOption}>
                      <input
                        type="radio"
                        name="feedbackTimeliness"
                        value={option.value}
                        checked={formData.feedbackTimeliness === option.value}
                        onChange={e => handleChange('feedbackTimeliness', e.target.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.feedbackTimeliness && <p style={styles.error}>{errors.feedbackTimeliness}</p>}
              </div>
              <div>
                <label style={styles.label}>
                  How valuable are the insights we send back? <span style={styles.required}>*</span>
                </label>
                <div style={styles.optionsRow}>
                  {ratingOptions.map(option => (
                    <label key={option.value} style={styles.radioOption}>
                      <input
                        type="radio"
                        name="feedbackQuality"
                        value={option.value}
                        checked={formData.feedbackQuality === option.value}
                        onChange={e => handleChange('feedbackQuality', e.target.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.feedbackQuality && <p style={styles.error}>{errors.feedbackQuality}</p>}
              </div>
              <div>
                <label style={styles.label}>
                  Can you apply the suggestions immediately in your live roles? <span style={styles.required}>*</span>
                </label>
                <div style={styles.optionsRow}>
                  {ratingOptions.map(option => (
                    <label key={option.value} style={styles.radioOption}>
                      <input
                        type="radio"
                        name="feedbackApplicability"
                        value={option.value}
                        checked={formData.feedbackApplicability === option.value}
                        onChange={e => handleChange('feedbackApplicability', e.target.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
                {errors.feedbackApplicability && <p style={styles.error}>{errors.feedbackApplicability}</p>}
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>What’s been the most helpful part so far?</label>
              <textarea
                value={formData.highlight || ''}
                onChange={e => handleChange('highlight', e.target.value)}
                placeholder="e.g. The messaging templates unlocked new conversations with DevOps leads…"
                style={styles.textarea}
                rows={3}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>What would make this course even more useful for you?</label>
              <textarea
                value={formData.improvementIdeas || ''}
                onChange={e => handleChange('improvementIdeas', e.target.value)}
                placeholder="Tell us what to refine, add, or remove…"
                style={styles.textarea}
                rows={3}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Would you like us to follow up with you personally?
              </label>
              <div style={styles.optionsRow}>
                {['No', 'Yes'].map(option => (
                  <label key={option} style={styles.radioOption}>
                    <input
                      type="radio"
                      name="followUpInterest"
                      value={option}
                      checked={formData.followUpInterest === option}
                      onChange={e => handleChange('followUpInterest', e.target.value)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {formData.followUpInterest === 'Yes' && (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Where should we reach you?
                  <span style={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  value={formData.followUpContact}
                  onChange={e => handleChange('followUpContact', e.target.value)}
                  placeholder="you@company.com"
                  style={styles.input}
                />
                {errors.followUpContact && <p style={styles.error}>{errors.followUpContact}</p>}
              </div>
            )}

            <div style={styles.actions}>
              <button
                type="button"
                onClick={handleDismiss}
                style={styles.secondaryButton}
              >
                Not now
              </button>
              <button type="submit" style={styles.primaryButton} disabled={loading}>
                {loading ? 'Sending…' : 'Share feedback'}
              </button>
            </div>
          </form>
        ) : (
          <div style={styles.thankYou}>
            <div style={styles.thankYouIcon}>✓</div>
            <h3 style={styles.thankYouTitle}>Thank you for shaping the course!</h3>
            <p style={styles.thankYouText}>
              We read every response and fold it into the next updates so you get more wins with senior talent.
            </p>
            <button onClick={handleThankYouContinue} style={styles.primaryButton}>
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    padding: '1rem'
  },
  modal: {
    background: 'white',
    borderRadius: '16px',
    maxWidth: '720px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '2rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    fontFamily: 'var(--raleway)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  title: {
    margin: 0,
    fontFamily: 'var(--montserrat)',
    fontSize: '1.5rem',
    color: 'var(--dark)'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '50%',
    transition: 'background 0.2s ease'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  intro: {
    color: 'var(--gray)',
    margin: 0,
    lineHeight: 1.6
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  fieldGroupGrid: {
    display: 'grid',
    gap: '1.5rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
  },
  label: {
    fontFamily: 'var(--montserrat)',
    fontWeight: 600,
    color: 'var(--dark)',
    lineHeight: 1.5
  },
  required: {
    color: '#dc2626',
    marginLeft: '0.25rem'
  },
  optionsRow: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.95rem'
  },
  textarea: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.95rem',
    fontFamily: 'var(--raleway)',
    resize: 'vertical',
    minHeight: '120px'
  },
  input: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.95rem',
    fontFamily: 'var(--raleway)'
  },
  error: {
    color: '#dc2626',
    fontSize: '0.85rem',
    margin: 0
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '0.5rem'
  },
  secondaryButton: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    color: '#4b5563',
    padding: '0.65rem 1.5rem',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 600,
    transition: 'all 0.2s ease'
  },
  primaryButton: {
    background: 'var(--orange)',
    border: 'none',
    color: 'var(--dark)',
    padding: '0.7rem 1.75rem',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 600,
    transition: 'all 0.2s ease'
  },
  thankYou: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '2rem 1rem'
  },
  thankYouIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: '#10B981',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.75rem'
  },
  thankYouTitle: {
    margin: 0,
    fontSize: '1.35rem',
    fontFamily: 'var(--montserrat)',
    color: 'var(--dark)'
  },
  thankYouText: {
    margin: 0,
    color: 'var(--gray)',
    lineHeight: 1.6,
    maxWidth: '420px'
  }
};

